import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://everythingautomation.ai';

const aboutPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Everything Automation',
  description: 'Everything Automation is a UK AI automation agency helping appointment-based businesses run smarter with intelligent booking, lead management, and customer communication systems.',
  url: `${siteUrl}/about`,
  publisher: {
    '@type': 'Organization',
    name: 'Everything Automation',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/bcf9f2411fadd279f7d1fd38d7e2d7e8/6cfd408f22ccc1f303d260d7c847c128.png',
    },
  },
};

const specializations = [
  {
    icon: 'ri-message-3-line',
    title: 'Customer Communication',
    desc: 'We automate every touchpoint, from first enquiry to post-sale follow-up, so your customers always feel heard and valued.',
  },
  {
    icon: 'ri-speed-line',
    title: 'Response Times',
    desc: 'Eliminate slow reply times for good. Our AI systems respond to leads instantly, day or night, dramatically improving conversion.',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Conversion Rates',
    desc: 'Every message, sequence, and follow-up is optimised to convert more interest into actual paying customers.',
  },
  {
    icon: 'ri-user-settings-line',
    title: 'Reducing Manual Workload',
    desc: 'Free your team from repetitive admin. Our automations handle the heavy lifting so your people can focus on high-value work.',
  },
];

const industries = [
  {
    icon: 'ri-scissors-line',
    name: 'Hair & Beauty Salons',
    desc: 'Fill every chair automatically. AI handles enquiries, confirms appointments, and sends reminders so no slot goes empty.',
    tags: ['Appointment Booking', 'No-Show Reduction', 'Client Follow-ups'],
  },
  {
    icon: 'ri-tooth-line',
    name: 'Dental & Medical Clinics',
    desc: 'Keep your diary full without the admin overhead. Automated booking and patient follow-ups around the clock.',
    tags: ['Patient Scheduling', 'Recall Reminders', 'Enquiry Handling'],
  },
  {
    icon: 'ri-hammer-line',
    name: 'Builders & Trades',
    desc: 'Turn quote requests into confirmed jobs. AI qualifies leads, books site visits, and chases outstanding enquiries for you.',
    tags: ['Quote Follow-ups', 'Site Visit Booking', 'Lead Qualification'],
  },
  {
    icon: 'ri-heart-pulse-line',
    name: 'Fitness & Wellness',
    desc: 'Keep your schedule packed. Automated class bookings, membership renewals, and session reminders handled instantly.',
    tags: ['Class Scheduling', 'Session Reminders', 'Membership Comms'],
  },
  {
    icon: 'ri-home-heart-line',
    name: 'Home Services',
    desc: 'Plumbers, cleaners, electricians. Book jobs, confirm arrival windows, and follow up for reviews all on autopilot.',
    tags: ['Job Scheduling', 'Arrival Confirmations', 'Review Requests'],
  },
  {
    icon: 'ri-stethoscope-line',
    name: 'Physio & Therapy',
    desc: 'Reduce admin stress for practitioners. AI manages new patient enquiries, appointment slots, and discharge follow-ups.',
    tags: ['New Patient Intake', 'Appointment Slots', 'Discharge Follow-ups'],
  },
];

const AboutPage = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.about-animate').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <title>About Everything Automation | AI Automation Specialists UK</title>
      <meta name="description" content="Learn about Everything Automation, the UK AI automation agency helping appointment-based businesses run smarter. We build intelligent booking, lead capture, and communication systems." />
      <meta name="keywords" content="Everything Automation about, AI automation agency UK, booking automation specialists, appointment business automation" />
      <link rel="canonical" href={`${siteUrl}/about`} />
      <meta property="og:title" content="About Everything Automation | AI Automation Specialists UK" />
      <meta property="og:description" content="We are a team of AI specialists helping UK businesses grow smarter with automation. From hair salons to dental clinics, builders to fitness studios, we automate it all." />
      <meta property="og:url" content={`${siteUrl}/about`} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutPageSchema) }} />
      <main className="bg-ea-black">
        {/* Hero */}
        <section className="relative pt-32 pb-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/d1ca075addd38111284042e15843a2b4.jpg"
              alt=""
              className="w-full h-full object-cover object-top opacity-15"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-ea-black via-ea-black/95 to-transparent"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
                <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">About Everything Automation</span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-7 leading-tight">
                Stop Losing Leads.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                  Start Booking Automatically.
                </span>
              </h1>
              <p className="text-ea-gray text-lg lg:text-xl leading-relaxed mb-8 max-w-2xl">
                We build AI systems that respond instantly, capture every lead, and book appointments automatically. No missed enquiries. No slow replies. Just consistent growth on autopilot.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-ea-blue hover:bg-ea-blue-light text-white font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ boxShadow: '0 0 20px rgba(14, 165, 233, 0.3)' }}
              >
                Work With Us <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="bg-ea-navy py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              {/* Mission */}
              <div className="bg-ea-dark-card border-l-2 border-ea-blue border border-ea-dark-border rounded-xl p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-ea-blue/5 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 mb-5">
                  <i className="ri-compass-3-line text-ea-blue text-xl"></i>
                </div>
                <p className="text-ea-blue text-xs font-bold uppercase tracking-widest mb-3">Our Mission</p>
                <h2 className="text-white font-bold text-2xl mb-4">Run Smarter, Not Harder</h2>
                <p className="text-ea-gray text-sm leading-relaxed">
                  We help businesses stop missing leads, chasing messages, and manually booking clients. Our systems handle enquiries, follow-ups, and bookings automatically so you can focus on running and growing your business.
                </p>
              </div>

              {/* Vision */}
              <div className="bg-ea-dark-card border-l-2 border-ea-blue border border-ea-dark-border rounded-xl p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-ea-blue/5 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 mb-5">
                  <i className="ri-eye-line text-ea-blue text-xl"></i>
                </div>
                <p className="text-ea-blue text-xs font-bold uppercase tracking-widest mb-3">Our Vision</p>
                <h2 className="text-white font-bold text-2xl mb-4">AI-Powered Businesses as Standard</h2>
                <p className="text-ea-gray text-sm leading-relaxed">
                  We believe every business should run with automation as the foundation. No missed leads. No slow replies. No wasted time. Just a system that works 24/7 capturing, converting, and booking clients automatically.
                </p>
              </div>
            </div>

            {/* Story */}
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Why We Built{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                  Everything Automation
                </span>
              </h2>
              <div className="space-y-4 text-ea-gray text-base leading-relaxed text-left max-w-3xl mx-auto">
                <p>
                  Most businesses don&apos;t lose clients because they&apos;re bad at what they do.
                </p>
                <p className="text-white/80 font-medium">They lose them because:</p>
                <ul className="space-y-2 pl-1">
                  {['They reply too late', 'They miss messages', 'They don\'t follow up', 'Their booking process is slow'].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-ea-gray text-sm">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-ea-blue shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-white/80 font-medium pt-2">That&apos;s where we come in.</p>
                <p>We build automated systems that:</p>
                <ul className="space-y-2 pl-1">
                  {['Respond instantly', 'Capture every lead', 'Follow up automatically', 'Book appointments for you'].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-ea-gray text-sm">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-ea-blue shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="pt-2">
                  So instead of chasing leads, your business runs like a 24/7 sales machine.
                </p>

                {/* What We Actually Do */}
                <div className="pt-4 border-t border-ea-dark-border">
                  <p className="text-ea-blue font-bold text-base mb-3">What We Actually Do</p>
                  <p className="mb-3">We build AI-powered systems that:</p>
                  <ul className="space-y-2 pl-1">
                    {[
                      'Answer customer enquiries instantly',
                      'Capture and organise every lead',
                      'Qualify leads automatically',
                      'Book appointments into your calendar',
                      'Follow up with leads until they convert',
                      'Re-engage past clients',
                      'Run across WhatsApp, Instagram, email, SMS, and more',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-ea-gray text-sm">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-ea-blue shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-ea-gray/60 text-sm mt-3">All connected into one system.</p>
                </div>

                {/* The Result */}
                <div className="pt-4 border-t border-ea-dark-border">
                  <p className="text-ea-blue font-bold text-base mb-3">The Result</p>
                  <ul className="space-y-2 pl-1">
                    {[
                      'More bookings',
                      'Faster response times',
                      'Less manual work',
                      'More time to focus on your business',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-ea-gray text-sm">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-ea-blue shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="pt-4 border-t border-ea-dark-border">
                  Today, we help businesses across the UK automate their sales and booking process, turning missed opportunities into consistent growth.
                </p>
              </div>
            </div>

            {/* Manual vs Automated Comparison */}
            <div className="max-w-5xl mx-auto mb-20">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
                  <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">The Difference</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  Manual Business vs.{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                    Automated Business
                  </span>
                </h2>
                <p className="text-ea-gray text-base max-w-xl mx-auto">
                  See exactly what changes when your business runs on automation.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-ea-dark-border">
                {/* Left: Without Automation */}
                <div className="bg-ea-dark-card border-r border-ea-dark-border p-8">
                  <div className="flex items-center gap-3 mb-7">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-rose-500/10 border border-rose-500/20">
                      <i className="ri-close-circle-line text-rose-400 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">Without Automation</p>
                      <p className="text-rose-400 text-xs">What most businesses deal with daily</p>
                    </div>
                  </div>
                  <ul className="space-y-4">
                    {[
                      { icon: 'ri-time-line', text: 'Enquiries come in outside hours. Nobody replies until morning. Lead goes cold.' },
                      { icon: 'ri-chat-off-line', text: 'Messages missed on WhatsApp, Instagram, and email. Potential clients move on.' },
                      { icon: 'ri-user-unfollow-line', text: 'No follow-up system. Leads that don\'t book first time are never chased.' },
                      { icon: 'ri-calendar-close-line', text: 'Booking requires back-and-forth messages. Friction kills conversions.' },
                      { icon: 'ri-error-warning-line', text: 'No-shows happen with no warning. Revenue lost, slot wasted.' },
                      { icon: 'ri-file-list-3-line', text: 'Staff spend hours on admin: answering the same questions, confirming appointments.' },
                      { icon: 'ri-user-heart-line', text: 'Past clients are never re-engaged. Repeat business left on the table.' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-rose-500/10 border border-rose-500/20 shrink-0 mt-0.5">
                          <i className={`${item.icon} text-rose-400 text-xs`}></i>
                        </div>
                        <p className="text-ea-gray text-sm leading-relaxed">{item.text}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Right: With Everything Automation */}
                <div className="bg-ea-black p-8 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-ea-blue/5 rounded-full -translate-y-24 translate-x-24 pointer-events-none"></div>
                  <div className="flex items-center gap-3 mb-7 relative">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20">
                      <i className="ri-checkbox-circle-line text-ea-blue text-lg"></i>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">With Everything Automation</p>
                      <p className="text-ea-blue text-xs">Your business on autopilot, 24/7</p>
                    </div>
                  </div>
                  <ul className="space-y-4 relative">
                    {[
                      { icon: 'ri-flashlight-line', badge: '&lt; 60 sec', text: 'Every enquiry answered instantly. Day or night, weekends, bank holidays. No lead left behind.' },
                      { icon: 'ri-customer-service-2-line', badge: 'All channels', text: 'One system covers WhatsApp, Instagram, email, SMS, and more. Nothing slips through.' },
                      { icon: 'ri-repeat-line', badge: 'Auto follow-up', text: 'Leads that don\'t book get followed up at 3, 7, and 14 days automatically. Until they convert.' },
                      { icon: 'ri-calendar-check-line', badge: 'Instant booking', text: 'Clients book directly into your calendar in seconds. No messages, no waiting, no friction.' },
                      { icon: 'ri-notification-3-line', badge: 'Auto reminders', text: 'Reminders sent before every appointment. No-show rates drop. Slots stay filled.' },
                      { icon: 'ri-robot-line', badge: 'Zero admin', text: 'AI handles every question, confirmation, and rebook. Your team focuses on the actual work.' },
                      { icon: 'ri-user-star-line', badge: 'Re-engagement', text: 'Past clients automatically contacted with offers and check-ins. Repeat revenue runs itself.' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-ea-blue/10 border border-ea-blue/20 shrink-0 mt-0.5">
                          <i className={`${item.icon} text-ea-blue text-xs`}></i>
                        </div>
                        <div>
                          <span className="inline-block text-[10px] font-bold text-ea-blue bg-ea-blue/10 border border-ea-blue/20 rounded-full px-2 py-0.5 mb-1 uppercase tracking-wide"
                            dangerouslySetInnerHTML={{ __html: item.badge }}
                          />
                          <p className="text-white/80 text-sm leading-relaxed">{item.text}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Bottom bar */}
              <div className="mt-4 bg-ea-blue/10 border border-ea-blue/20 rounded-xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-white/80 text-sm text-center sm:text-left">
                  <strong className="text-white">5,200+ bookings automated</strong> for businesses across the UK. Average response time under 60 seconds.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-ea-blue hover:bg-ea-blue-light text-white font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm shrink-0"
                  style={{ boxShadow: '0 0 16px rgba(14, 165, 233, 0.3)' }}
                >
                  Get Your System Built <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            {/* Four-Tier Plans Overview */}
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
                  <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">How We Work With You</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Four Tiers, One Goal</h2>
                <p className="text-ea-gray text-base max-w-2xl mx-auto">
                  Whether you&apos;re just getting started or ready for a fully bespoke operating system, we have a plan built around where you are and where you want to go.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    name: 'Starter',
                    price: '£149/mo',
                    icon: 'ri-seedling-line',
                    headline: 'Automated Lead Capture',
                    desc: 'Perfect for businesses ready to stop losing leads. AI captures and qualifies enquiries around the clock.',
                    color: 'border-ea-blue/20',
                    badge: null,
                  },
                  {
                    name: 'Pro',
                    price: '£299/mo',
                    icon: 'ri-rocket-line',
                    headline: 'Sales & Booking System',
                    desc: 'Full automation for bookings, follow-ups, and sales sequences. Our most popular starting point.',
                    color: 'border-ea-blue/50',
                    badge: 'Most Popular',
                  },
                  {
                    name: 'Scale',
                    price: '£599/mo',
                    icon: 'ri-building-4-line',
                    headline: 'Business Operating System',
                    desc: 'A fully automated business engine. CRM, multi-channel comms, and advanced reporting all included.',
                    color: 'border-ea-blue/20',
                    badge: null,
                  },
                  {
                    name: 'Custom',
                    price: 'From £799/mo',
                    icon: 'ri-settings-5-line',
                    headline: 'Fully Custom System',
                    desc: 'Built from scratch around your exact business needs. For businesses with complex or unique requirements.',
                    color: 'border-ea-blue/20',
                    badge: null,
                  },
                ].map((plan) => (
                  <div
                    key={plan.name}
                    className={`bg-ea-dark-card border ${plan.color} rounded-xl p-5 flex flex-col gap-3 relative`}
                  >
                    {plan.badge && (
                      <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-ea-blue text-white text-[10px] font-bold rounded-full whitespace-nowrap">
                        {plan.badge}
                      </span>
                    )}
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20">
                      <i className={`${plan.icon} text-ea-blue text-base`}></i>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">{plan.name}</p>
                      <p className="text-ea-blue text-xs font-semibold">{plan.price}</p>
                    </div>
                    <p className="text-white/80 text-xs font-medium leading-snug">{plan.headline}</p>
                    <p className="text-ea-gray text-xs leading-relaxed flex-1">{plan.desc}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-7">
                <a
                  href="/pricing"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-ea-blue/10 hover:bg-ea-blue/20 text-ea-blue border border-ea-blue/30 font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                >
                  View Full Pricing Details <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Specialisations */}
        <section className="bg-ea-black py-20" ref={sectionRef}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
                <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">What We Specialise In</span>
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">Our Core Expertise</h2>
              <p className="text-ea-gray text-lg max-w-xl mx-auto">
                Four pillars that define how we transform businesses through AI automation.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {specializations.map((spec) => (
                <div
                  key={spec.title}
                  className="about-animate bg-ea-dark-card border border-ea-dark-border hover:border-ea-blue/50 rounded-xl p-4 sm:p-6 text-center transition-all duration-300 group cursor-default"
                  style={{
                    opacity: 0,
                    transform: 'translateY(20px)',
                    transition: 'opacity 0.5s ease, transform 0.5s ease, border-color 0.3s ease',
                  }}
                >
                  <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center rounded-xl bg-ea-blue/10 border border-ea-blue/20 group-hover:bg-ea-blue/20 transition-colors duration-300 mx-auto mb-3 sm:mb-5">
                    <i className={`${spec.icon} text-ea-blue text-xl sm:text-2xl`}></i>
                  </div>
                  <h3 className="text-white font-semibold text-xs sm:text-base mb-2 sm:mb-3">{spec.title}</h3>
                  <p className="text-ea-gray text-xs leading-relaxed">{spec.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries We Serve */}
        <section className="bg-ea-navy py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
                <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Who We Work With</span>
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">Industries We Serve</h2>
              <p className="text-ea-gray text-lg max-w-2xl mx-auto">
                We build automation for appointment-based businesses that rely on bookings. If your business runs on a schedule, we can make it run itself.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {industries.map((industry) => (
                <div
                  key={industry.name}
                  className="bg-ea-dark-card border border-ea-dark-border hover:border-ea-blue/40 rounded-xl p-3 sm:p-7 group transition-all duration-300 cursor-default"
                >
                  <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-4 mb-3 sm:mb-5">
                    <div className="w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl bg-ea-blue/10 border border-ea-blue/20 group-hover:bg-ea-blue/20 transition-colors duration-300 shrink-0">
                      <i className={`${industry.icon} text-ea-blue text-base sm:text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-xs sm:text-base mb-0.5 sm:mb-1">{industry.name}</h3>
                      <p className="text-ea-gray text-[11px] sm:text-sm leading-relaxed">{industry.desc}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 sm:gap-2 pt-2 sm:pt-4 border-t border-ea-dark-border">
                    {industry.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[10px] sm:text-xs px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-ea-blue/10 text-ea-blue border border-ea-blue/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-ea-gray text-sm mb-5">
                Don&apos;t see your industry? If you take bookings or appointments, we can automate it.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-7 py-3 bg-ea-blue/10 hover:bg-ea-blue/20 text-ea-blue border border-ea-blue/30 font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
              >
                Talk to us about your industry <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-ea-navy py-20">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-5">
              Stop Losing Leads.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                Let&apos;s Fix That.
              </span>
            </h2>
            <p className="text-ea-gray text-lg mb-8">
              Let&apos;s talk about your business and build an AI automation system that fits your goals.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-ea-blue hover:bg-ea-blue-light text-white font-bold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-base"
              style={{ boxShadow: '0 0 25px rgba(14, 165, 233, 0.3)' }}
            >
              See How It Works <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default AboutPage;
